import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PropTypes from "prop-types";
import Swal from "sweetalert2";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

// Custom hooks
import useMessageData from "layouts/tables/data/smsTableData";

function Message() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMessage,
    setSelectedMessage,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMessage,
    members,
    sendBulkMessages,
    smsSummary,
  } = useMessageData();

  const [messageType, setMessageType] = useState("single");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  const allSelected = useMemo(() => {
    return members.length > 0 && selectedMembers.length === members.length;
  }, [members, selectedMembers]);

  const selectedMemberObjects = useMemo(
    () => members.filter((member) => selectedMembers.includes(member.id)),
    [members, selectedMembers]
  );

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members.map((member) => member.id));
    }
  };

  const handleMemberSelection = (event, newValue) => {
    setSelectedMembers(newValue.map((member) => member.id));
  };

  const handleMessageTypeChange = (event) => {
    setMessageType(event.target.value);
    if (event.target.value === "single") {
      setSelectedMembers([]);
    }
  };

  const handleSubmit = () => {
    if (messageType === "bulk") {
      if (selectedMembers.length === 0) {
        Swal.fire({
          title: "No Recipients Selected",
          text: "Please select at least one recipient",
          icon: "warning",
        });
        return;
      }
      if (!selectedMessage?.message) {
        Swal.fire({
          title: "Message Required",
          text: "Please enter a message to send",
          icon: "warning",
        });
        return;
      }
      setPendingPayload({
        kind: "bulk",
        memberIds: selectedMembers,
        message: selectedMessage.message,
      });
    } else {
      if (!selectedMessage?.recipient || !selectedMessage?.message) {
        Swal.fire({
          title: "Incomplete Information",
          text: !selectedMessage?.recipient
            ? "Please select a recipient"
            : "Please enter a message",
          icon: "warning",
        });
        return;
      }
      const memberObj = members.find((m) => m.id === selectedMessage?.member_id);
      setPendingPayload({
        kind: "single",
        message: { ...selectedMessage, type: selectedMessage.type || "single" },
        memberObj,
      });
    }
    setEditModalOpen(false);
    setChannelDialogOpen(true);
  };

  const handleChannelSend = async (channel) => {
    setChannelDialogOpen(false);
    if (!pendingPayload) return;
    const { kind, message, memberObj, memberIds } = pendingPayload;

    if (channel === "sms") {
      setIsSending(true);
      try {
        if (kind === "bulk") {
          await sendBulkMessages(memberIds, message, "bulk");
        } else {
          await handleEditSubmit(message);
        }
      } catch {
        Swal.fire({ title: "Error!", text: "Failed to send message.", icon: "error" });
      } finally {
        setIsSending(false);
      }
    } else if (channel === "whatsapp") {
      if (kind === "single") {
        const phone = memberObj?.phone?.replace(/\D/g, "") || "";
        if (!phone) {
          Swal.fire("No phone number", "This member has no phone number on file.", "warning");
          return;
        }
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message.message)}`, "_blank");
      } else {
        const msgText = message;
        const targets = members.filter((m) => memberIds.includes(m.id) && m.phone);
        if (!targets.length) {
          Swal.fire("No phone numbers", "No selected members have phone numbers.", "warning");
          return;
        }
        // Open first member, show count info
        const cleaned = targets[0].phone.replace(/\D/g, "");
        window.open(`https://wa.me/${cleaned}?text=${encodeURIComponent(msgText)}`, "_blank");
        if (targets.length > 1) {
          Swal.fire({
            title: "WhatsApp Bulk",
            html: `Opened WhatsApp for <strong>${targets[0].first_name} ${targets[0].surname}</strong>.<br/>
                   <em>For bulk WhatsApp, open each member individually from the Members page or use SMS for mass messaging.</em>`,
            icon: "info",
          });
        }
      }
    } else if (channel === "email") {
      if (kind === "single") {
        const email = memberObj?.email;
        if (!email) {
          Swal.fire("No email", "This member has no email address on file.", "warning");
          return;
        }
        window.open(`mailto:${email}?body=${encodeURIComponent(message.message)}`, "_blank");
      } else {
        const msgText = message;
        const targets = members.filter((m) => memberIds.includes(m.id) && m.email);
        if (!targets.length) {
          Swal.fire("No emails", "No selected members have email addresses.", "warning");
          return;
        }
        const emails = targets.map((m) => m.email).join(",");
        window.open(`mailto:${emails}?body=${encodeURIComponent(msgText)}`, "_blank");
      }
    }
    setPendingPayload(null);
  };

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading Messages...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="SMS Messages"
        subtitle="Send and track text messages to members."
        actions={
          <>
            <Chip
              icon={<AccountBalanceWalletIcon fontSize="small" />}
              label={
                smsSummary?.isLoading
                  ? "Loading balance..."
                  : `Balance: ${smsSummary?.balance ?? 0} credits`
              }
              color="info"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddMessage}>
              Send Message
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted={false}
          entriesPerPage={true}
          showTotalEntries={true}
          noEndBorder
          canSearch={true}
          searchLabel="Search messages..."
        />
      </Card>

      {/* Single-recipient Send Message modal */}
      <FormDialog
        open={editModalOpen && messageType === "single"}
        onClose={() => {
          if (!isSending) setEditModalOpen(false);
        }}
        title={selectedMessage?.id ? "Edit Message" : "Send New Message"}
        onSave={handleSubmit}
        saveLabel={selectedMessage?.id ? "Update Message" : "Send Message"}
        loading={isSending}
        maxWidth="sm"
      >
        <FormSection>
          <FormControl fullWidth margin="normal">
            <InputLabel id="message-type-label">Message Type</InputLabel>
            <Select
              labelId="message-type-label"
              id="message-type"
              value={messageType}
              label="Message Type"
              onChange={handleMessageTypeChange}
              disabled={isSending}
            >
              <MenuItem value="single">Single Message</MenuItem>
              <MenuItem value="bulk">Bulk Message</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" required>
            <InputLabel id="recipient-label">Recipient</InputLabel>
            <Select
              labelId="recipient-label"
              id="recipient"
              value={selectedMessage?.member_id || ""}
              label="Recipient"
              onChange={(e) => {
                const selectedMember = members.find((m) => m.id === e.target.value);
                setSelectedMessage({
                  ...selectedMessage,
                  member_id: e.target.value,
                  recipient: selectedMember?.phone || "",
                });
              }}
              disabled={isSending}
            >
              {members.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.first_name} {member.surname} ({member.phone})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormSection>

        <FormSection>
          <TextField
            label="Message"
            multiline
            rows={6}
            value={selectedMessage?.message || ""}
            onChange={(e) =>
              setSelectedMessage({
                ...selectedMessage,
                message: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            required
            disabled={isSending}
          />
        </FormSection>
      </FormDialog>

      {/* Bulk Send modal */}
      <FormDialog
        open={editModalOpen && messageType === "bulk"}
        onClose={() => {
          if (!isSending) {
            setEditModalOpen(false);
            setSelectedMembers([]);
          }
        }}
        title="Send Bulk Message"
        onSave={handleSubmit}
        saveLabel="Send Message"
        loading={isSending}
        maxWidth="md"
      >
        <FormSection>
          <FormControl fullWidth margin="normal">
            <InputLabel id="bulk-message-type-label">Message Type</InputLabel>
            <Select
              labelId="bulk-message-type-label"
              id="bulk-message-type"
              value={messageType}
              label="Message Type"
              onChange={handleMessageTypeChange}
              disabled={isSending}
            >
              <MenuItem value="single">Single Message</MenuItem>
              <MenuItem value="bulk">Bulk Message</MenuItem>
            </Select>
          </FormControl>
        </FormSection>

        <FormSection>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={members}
            getOptionLabel={(member) => `${member.first_name} ${member.surname} (${member.phone})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedMemberObjects}
            onChange={handleMemberSelection}
            disabled={isSending}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={`${option.first_name} ${option.surname}`}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Recipients"
                placeholder="Search members..."
                margin="normal"
                helperText={`${selectedMembers.length} of ${members.length} members selected`}
              />
            )}
          />
          <Button size="small" onClick={handleSelectAll} disabled={isSending} sx={{ mt: 1 }}>
            {allSelected ? "Deselect All" : "Select All"}
          </Button>
        </FormSection>

        <FormSection>
          <TextField
            label="Message"
            multiline
            rows={6}
            value={selectedMessage?.message || ""}
            onChange={(e) =>
              setSelectedMessage({
                ...selectedMessage,
                message: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            required
            disabled={isSending}
          />
        </FormSection>
      </FormDialog>

      {/* Delete Message Modal */}
      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message="Are you sure you want to delete this message?"
        confirmLabel="Confirm Delete"
      />

      {/* Channel Picker Dialog */}
      <Dialog
        open={channelDialogOpen}
        onClose={() => setChannelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Send via…</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how you would like to deliver this message.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              startIcon={<SmsOutlinedIcon />}
              onClick={() => handleChannelSend("sms")}
            >
              SMS
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<WhatsAppIcon />}
              onClick={() => handleChannelSend("whatsapp")}
              sx={{ bgcolor: "#25D366", "&:hover": { bgcolor: "#1ebe57" }, color: "#fff" }}
            >
              WhatsApp
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="info"
              size="large"
              startIcon={<EmailOutlinedIcon />}
              onClick={() => handleChannelSend("email")}
            >
              Email
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChannelDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

Message.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  handlePrint: PropTypes.func,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedMessage: PropTypes.object,
  setSelectedMessage: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddMessage: PropTypes.func,
  members: PropTypes.arrayOf(PropTypes.object),
};

export default Message;
